import { FileTextIcon, PackageIcon, RepeatIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { useAdmin } from '../AdminContext';
import { btnGhost, listBox } from '../ui/styles';
import { orderTotal } from '../../utils/account';
import { orderLabel } from '../../utils/accountOrders';
import { printInvoice } from '../../utils/invoice';
import { euro } from '../../core/format/currency';
import { dayLong, dayMedium, newestFirst } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import type { AccountOrder } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';
const statusTone: Record<AccountOrder['status'], 'accent' | 'soft' | 'neutral'> = {
  'In behandeling': 'accent',
  Onderweg: 'soft',
  Bezorgd: 'neutral'
};

/** Zelfde badge als in Bestellingen (admin/orders/orderLabels.ts `orderBadge`): geannuleerd gaat voor. */
const badgeOf = (o: AccountOrder): {
  label: string;
  tone: 'accent' | 'soft' | 'neutral';
} => o.cancelledAt ? {
  label: 'Geannuleerd',
  tone: 'neutral'
} : {
  label: o.status,
  tone: statusTone[o.status]
};

/** Bestellingen van één klant, met de factuur zoals zij die ook kan downloaden. */
export function CustomerOrders({
  ctl
}: {
  ctl: CustomerFileControl;
}) {
  const {
    index,
    site
  } = useAdmin();
  const {
    account
  } = ctl.file;
  const orders = newestFirst(account.orders);
  // Een geannuleerde bestelling telt niet mee in wat ze samen besteedde.
  const spent = orders.filter(o => !o.cancelledAt).reduce((s, o) => s + orderTotal(o), 0);
  if (orders.length === 0) {
    return <EmptyState icon={PackageIcon} title={`${account.customer.firstName} heeft nog niets besteld`} text="Salonbezoeken worden in de salon afgerekend en staan hier niet bij." data-id="element-1231" data-mp="2bus82rf3y3w7" />;
  }
  const openInvoice = async (o: AccountOrder) => {
    if (!(await printInvoice(o, account.customer, site.salon, index.productById, dayLong(o.date)))) toast.error('De factuur kon niet openen', {
      description: 'Probeer het nog eens.'
    });
  };
  return <div data-id="element-1232" data-mp="8jm3ut5hik5a">
      <p className="mb-4 text-[14px] text-muted" data-id="element-1233" data-mp="8iu8njfsuofj">
        {plural(orders.length, 'bestelling', 'bestellingen')} · samen {euro(spent, {
        cents: true
      })}
      </p>
      <ul className={listBox} data-id="element-1234" data-mp="3us55793lyuhv">
        {orders.map(o => {
        const badge = badgeOf(o);
        const label = orderLabel(o);
        return <li key={o.id} className="px-4 py-4 sm:px-5" data-id="element-1235" data-mp="wc2jyy5nw21y">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-1236" data-mp="rby5fdobdhed">
                <Badge tone={badge.tone} data-id="element-1237" data-mp="mjogo0yxqndo">{badge.label}</Badge>
                <span className="min-w-0 flex-1" data-id="element-1238" data-mp="mi3m4bcmf4ft">
                  <span className="block text-[14px] text-ink" data-id="element-1239" data-mp="hlva36tpzrcv">
                    {label}
                    {o.viaSubscription && <span className="ml-2 inline-flex items-center gap-1 align-middle text-[12px] text-clay" data-id="element-1240" data-mp="1f1si9v8mv5ym">
                        <RepeatIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-1241" data-mp="1a7xzcu0uqv0l" />
                        Bijvuldoos
                      </span>}
                  </span>
                  <span className="block text-[13px] text-muted" data-id="element-1242" data-mp="1mltjqpbj2k97">{dayMedium(o.date)}</span>
                </span>
                <span className={`text-[14px] tabular-nums ${o.cancelledAt ? 'text-muted line-through' : 'text-ink'}`} data-id="element-1243" data-mp="1whwmnxe7emx7">{euro(orderTotal(o), {
                cents: true
              })}</span>
                {!o.cancelledAt && <button type="button" onClick={() => openInvoice(o)} className={btnGhost} aria-label={`Factuur van bestelling ${label}`} data-id="element-1244" data-mp="25imgwwntg58t">
                    <FileTextIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1245" data-mp="2aavzfrmlq312" />
                    Factuur
                  </button>}
              </div>
              <p className="mt-2 truncate text-[13px] text-muted" data-id="element-1246" data-mp="2k8tmwcwmzvsm">
                {o.lines.map(l => `${l.qty} × ${index.productById(l.productId)?.name ?? l.productId}`).join(' · ')}
              </p>
            </li>;
      })}
      </ul>
    </div>;
}